import { isEmpty, mapValues } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import TextInput from '../ReusableComponents/TextInput/TextInput';
import makeTaxClassificationSelectInput from '../../Lib/Utils/FormUtils/make-tax-classification-select-input';
import {
  DomesticTaxClassifications,
  InternationalTaxClassifications,
} from '../../Lib/Constants/tax-classifications';
import styles from './InfluencerProfile.module.css';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import maskTaxId from '../../Lib/Utils/InfluencerForm/mask-tax-id';
import isNumeric from '../../Lib/Utils/Numeric/isNumeric';

export default function TaxFields(props) {
  const { domestic = true, form, setTaxFieldsErrors } = props;
  const [errors, setErrors] = useState(mapValues(form, () => false));
  const taxClassificationNumberLabel = useMemo(() => {
    if (props.domestic) {
      return props.form.is_individual
        ? 'Social Security Number'
        : 'EIN (Employer Identification Number)';
    }
    return 'Foreign Tax ID';
  }, [props.domestic, props.form.is_individual]);

  function validateTaxId(taxId) {
    return isEmpty(taxId) || !isNumeric(taxId) || taxId.length < 9;
  }

  function maybeSliceValue(name, value) {
    if (name === 'tax_id' && value) return value.slice(0, 9);
    return value;
  }

  const onChange = (e) => {
    const { name, value } = e.target;
    setErrors((err) => ({ ...err, [name]: false }));
    const val = maybeSliceValue(name, value);
    props.onChange(name, val);
  };

  useEffect(() => {
    if (Object.keys(errors).some((key) => errors[key])) {
      setTaxFieldsErrors(true);
    } else {
      setTaxFieldsErrors(false);
    }
  }, [errors]);

  useEffect(() => {
    if (
      !props.domestic &&
      !props.disabled &&
      props.form.tax_classification !== 'International'
    ) {
      onChange({
        target: {
          name: 'tax_classification',
          value: 'International',
        },
      });
    } else if (
      props.domestic &&
      !props.disabled &&
      props.form.tax_classification === 'International'
    ) {
      onChange({
        target: {
          name: 'tax_classification',
          value: null,
        },
      });
    }
  }, [props.domestic, props.form.tax_classification]);

  useEffect(() => {
    if (
      !props.disabled &&
      isEmpty(props.form.tax_classification) &&
      props.form.is_individual
    ) {
      const defaultClassification = DomesticTaxClassifications[0];

      onChange({
        target: {
          name: 'tax_classification',
          value: defaultClassification.value,
        },
      });
    }
  }, [props.form.is_individual]);

  useEffect(() => {
    if (!props.disabled && domestic && props.form.is_individual) {
      onChange({
        target: {
          name: 'tax_classification',
          value: DomesticTaxClassifications[0].text,
        },
      });
    }
  }, [domestic, props.form.is_individual]);

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {domestic && (
        <TextInput
          fullWidth
          required
          disabled={props.disabled}
          error={errors.tax_id}
          label={taxClassificationNumberLabel}
          variant="outlined"
          onChange={onChange}
          onBlur={() => {
            setErrors((e) => ({
              ...e,
              tax_id: validateTaxId(form.tax_id),
            }));
          }}
          name="tax_id"
          value={maskTaxId(form.tax_id, props.disabled) || ''}
          id="va-tax-id"
          autoComplete="no"
          formHelperText={
            !isNumeric(form.tax_id) &&
            errors.tax_id &&
            'No special characters allowed'
          }
        />
      )}

      <SelectInput
        fullWidth
        required
        disabled={props.disabled || !props.domestic || props.form.is_individual}
        error={errors.tax_classification}
        label="Tax Classification"
        native
        id="va-tax-classification"
        value={form.tax_classification || ''}
        onChange={onChange}
        onBlur={() => {
          setErrors((e) => ({
            ...e,
            tax_classification: isEmpty(form.tax_classification),
          }));
        }}
        input={makeTaxClassificationSelectInput(form)}
        name="tax_classification"
        inputProps={{
          name: 'tax_classification',
          id: 'tax_classification',
        }}
        options={
          props.domestic
            ? DomesticTaxClassifications
            : InternationalTaxClassifications
        }
        className={styles.rowItem}
      />
    </div>
  );
}

TaxFields.propTypes = {
  disabled: PropTypes.bool.isRequired,
  domestic: PropTypes.bool,
  form: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
};
